import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Lock, ArrowLeft, Terminal, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (/invalide|expire/i.test(detail)) {
        setError('Ce lien est invalide ou a expiré (il est valable 1 heure). Demandez-en un nouveau.');
      } else if (err.response?.status === 429) {
        setError('Trop de tentatives. Patientez une minute avant de réessayer.');
      } else {
        setError(detail || 'Une erreur est survenue. Réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-black border border-zinc-800 p-3 pl-10 pr-10 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all placeholder:text-zinc-500';

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-8">

        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <ShieldCheck className="w-10 h-10 text-blue-500" />
            <span className="font-mono text-2xl font-black text-white uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 mb-4">
            <Terminal className="w-3 h-3 text-blue-400" />
            <span className="text-blue-400 text-[10px] font-mono uppercase tracking-widest">Password_Reset</span>
          </div>
          <h1 className="text-2xl font-black text-white font-mono uppercase tracking-tighter">Nouveau mot de passe</h1>
          <p className="text-zinc-400 text-sm mt-2">Choisissez un nouveau mot de passe pour votre compte.</p>
        </div>

        {!token ? (
          /* Lien incomplet */
          <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-8 text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h2 className="text-white font-mono font-bold uppercase">Lien incomplet</h2>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Ce lien de réinitialisation ne contient pas de code valide. Ouvrez le lien directement depuis l'email reçu, ou demandez-en un nouveau.
            </p>
            <Link to="/forgot-password" className="inline-flex items-center gap-2 text-blue-400 font-mono text-xs uppercase hover:text-blue-300 transition-colors">
              Demander un nouveau lien
            </Link>
          </div>
        ) : done ? (
          /* Succès */
          <div className="bg-green-500/5 border border-green-500/30 rounded-sm p-8 text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <h2 className="text-white font-mono font-bold uppercase">Mot de passe modifié</h2>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Votre mot de passe a bien été réinitialisé. Vous pouvez maintenant vous connecter.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 font-black font-mono uppercase text-sm rounded-none transition-all"
            >
              Se connecter
            </button>
          </div>
        ) : (
          /* Formulaire */
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500/0 via-blue-500 to-blue-500/0 opacity-50" />

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div role="alert" className="flex items-start gap-2 p-3 bg-red-500/10 border-l-2 border-red-500 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {error}
                    {/expiré|invalide/.test(error) && (
                      <> <Link to="/forgot-password" className="underline hover:text-red-300">Nouveau lien</Link></>
                    )}
                  </span>
                </div>
              )}

              <div className="space-y-1">
                <label htmlFor="new-password" className="text-xs font-bold text-blue-400 font-mono tracking-widest uppercase">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={inputClass}
                    aria-describedby="password-help"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p id="password-help" className={`text-xs ${tooShort ? 'text-red-400' : 'text-zinc-400'}`}>
                  8 caractères minimum.
                </p>
              </div>

              <div className="space-y-1">
                <label htmlFor="confirm-password" className="text-xs font-bold text-blue-400 font-mono tracking-widest uppercase">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                {mismatch && <p className="text-xs text-red-400">Les deux mots de passe ne correspondent pas.</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-4 font-black font-mono uppercase text-sm rounded-none transition-all"
              >
                {loading ? <span className="animate-pulse">Enregistrement…</span> : 'Enregistrer le mot de passe'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white font-mono text-xs uppercase transition-colors">
                <ArrowLeft className="w-3 h-3" /> Retour à la connexion
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
